import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "making-your-plugin-theme-aware"
    }}>{`Making Your Plugin Theme-aware`}</h1>
    <h2 {...{
      "id": "overview"
    }}>{`Overview`}</h2>
    <p>{`Photoshop allows the user to switch the UI between four themes:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "483px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "19.375%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/af3b5bda253fd5e5bc711d55453117d4/5530d/ps-theme-selection.webp 320w", "/photoshop/uxp/2021/static/af3b5bda253fd5e5bc711d55453117d4/b685b/ps-theme-selection.webp 483w"],
            "sizes": "(max-width: 483px) 100vw, 483px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/af3b5bda253fd5e5bc711d55453117d4/dd4a7/ps-theme-selection.png 320w", "/photoshop/uxp/2021/static/af3b5bda253fd5e5bc711d55453117d4/a3f7c/ps-theme-selection.png 483w"],
            "sizes": "(max-width: 483px) 100vw, 483px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/2021/static/af3b5bda253fd5e5bc711d55453117d4/a3f7c/ps-theme-selection.png",
            "alt": "Photoshop Theme Colors",
            "title": "Photoshop Theme Colors",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <ul>
      <li parentName="ul">{`Darkest`}</li>
      <li parentName="ul">{`Dark`}</li>
      <li parentName="ul">{`Light`}</li>
      <li parentName="ul">{`Lightest`}</li>
    </ul>
    <p>{`If your plugin is not theme aware, it can look out of place (or possibly have unreadable text) when the user switches themes. It's very straightforward to add theme awareness to your plugin, so there's really no reason not to.`}</p>
    <h2 {...{
      "id": "built-in-theme-colors"
    }}>{`Built-in Theme Colors`}</h2>
    <p>{`The easiest way to make your plugin theme-aware is to make use of a CSS feature called `}<inlineCode parentName="p">{`CSS variables`}</inlineCode>{`. CSS variables are just what they sound like: a way to have dynamic property definitions in CSS.`}</p>
    <p>{`Luckily, Photoshop exposes a number of theme-specific CSS variables that are perfect for making your plugin respond to the user's theme color choice. These variables are:`}</p>
    <ul>
      <li parentName="ul">{`--uxp-host-background-color`}</li>
      <li parentName="ul">{`--uxp-host-text-color`}</li>
      <li parentName="ul">{`--uxp-host-border-color`}</li>
      <li parentName="ul">{`--uxp-host-link-text-color`}</li>
      <li parentName="ul">{`--uxp-host-widget-hover-background-color`}</li>
      <li parentName="ul">{`--uxp-host-widget-hover-text-color`}</li>
      <li parentName="ul">{`--uxp-host-widget-hover-border-color`}</li>
    </ul>
    <p>{`And a few more: `}</p>
    <ul>
      <li parentName="ul">{`--uxp-host-text-color-secondary`}</li>
      <li parentName="ul">{`--uxp-host-link-hover-text-color`}</li>
      <li parentName="ul">{`--uxp-host-label-text-color`}</li>
    </ul>
    <p>{`Finally, there are three dealing with font size:`}</p>
    <ul>
      <li parentName="ul">{`--uxp-host-font-size`}</li>
      <li parentName="ul">{`--uxp-host-font-size-smaller`}</li>
      <li parentName="ul">{`--uxp-host-font-size-larger`}</li>
    </ul>
    <h2 {...{
      "id": "how-to-use-css-variables"
    }}>{`How to Use CSS Variables`}</h2>
    <p>{`In the CSS for your plugin panel, use the built-in CSS variables like this:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`body {
    background-color: var(--uxp-host-background-color);
    color: var(--uxp-host-text-color);
    border-color: var(--uxp-host-border-color);
    font-size: var(--uxp-host-font-size);
}
`}</code></pre>
    <p>{`Of course, you don't have to style the entire body of your panel with these variables; you could style individual CSS classes instead.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-css"
      }}>{`.bigText {
    color: var(--uxp-host-text-color);
    font-size: var(--uxp-host-font-size-larger);
}
.smallText {
    color: var(--uxp-host-text-color);
    font-size: var(--uxp-host-font-size-smaller);
}
`}</code></pre>
    <h2 {...{
      "id": "how-do-they-look"
    }}>{`How Do They Look?`}</h2>
    <p>{`If you create a simple HTML file that includes these CSS variables, here's how they'll look in Photoshop.`}</p>
    <p>{`On the "darkest" theme, they look like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "562px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "71.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/76fdbbcc4e3a00a43d39ef249ae35030/5530d/fonts-darkest.webp 320w", "/photoshop/uxp/2021/static/76fdbbcc4e3a00a43d39ef249ae35030/d0171/fonts-darkest.webp 562w"],
            "sizes": "(max-width: 562px) 100vw, 562px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/76fdbbcc4e3a00a43d39ef249ae35030/dd4a7/fonts-darkest.png 320w", "/photoshop/uxp/2021/static/76fdbbcc4e3a00a43d39ef249ae35030/a4f00/fonts-darkest.png 562w"],
            "sizes": "(max-width: 562px) 100vw, 562px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/2021/static/76fdbbcc4e3a00a43d39ef249ae35030/a4f00/fonts-darkest.png",
            "alt": "Darkest Theme",
            "title": "Darkest Theme",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>
    <p>{`And on the "lightest" theme, they look like this:`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "562px"
        }
      }}>{`
      `}<span parentName="span" {...{
          "className": "gatsby-resp-image-background-image",
          "style": {
            "paddingBottom": "71.875%",
            "position": "relative",
            "bottom": "0",
            "left": "0",
            "display": "block",
            "transition": "opacity 0.5s 0.5s",
            "pointerEvents": "none"
          }
        }}></span>{`
  `}<picture parentName="span">{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/de620784d8f852fe019b304c822c90b5/5530d/fonts-lightest.webp 320w", "/photoshop/uxp/2021/static/de620784d8f852fe019b304c822c90b5/d0171/fonts-lightest.webp 562w"],
            "sizes": "(max-width: 562px) 100vw, 562px",
            "type": "image/webp"
          }}></source>{`
          `}<source parentName="picture" {...{
            "srcSet": ["/photoshop/uxp/2021/static/de620784d8f852fe019b304c822c90b5/dd4a7/fonts-lightest.png 320w", "/photoshop/uxp/2021/static/de620784d8f852fe019b304c822c90b5/a4f00/fonts-lightest.png 562w"],
            "sizes": "(max-width: 562px) 100vw, 562px",
            "type": "image/png"
          }}></source>{`
          `}<img parentName="picture" {...{
            "className": "gatsby-resp-image-image",
            "src": "/photoshop/uxp/2021/static/de620784d8f852fe019b304c822c90b5/a4f00/fonts-lightest.png",
            "alt": "Lightest Theme",
            "title": "Lightest Theme",
            "loading": "lazy",
            "style": {
              "width": "100%",
              "height": "100%",
              "margin": "0",
              "verticalAlign": "middle",
              "position": "absolute",
              "opacity": "0",
              "transition": "opacity 0.5s",
              "color": "inherit",
              "boxShadow": "inset 0px 0px 0px 400px none",
              "top": "0",
              "left": "0"
            }
          }}></img>{`
        `}</picture>{`
    `}</span></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      